import { useEffect, type RefObject } from 'react';
import { BrainCircuit, BriefcaseBusiness, Eye, Layers3, ShieldCheck, Target } from 'lucide-react';
import './page-immersion.css';

const clamp = (value: number) => Math.max(0, Math.min(1, value));

// Native scrolling remains in charge. Decorative layers update once per scroll
// frame; no animation loop, pointer capture, or hidden essential content.
export function usePageImmersion(root: RefObject<HTMLDivElement | null>, enabled = true) {
  useEffect(() => {
    const page = root.current;
    if (!page || !enabled) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const scene = page.querySelector<HTMLElement>('[data-immersion-scene]');
    const reading = page.querySelector<HTMLElement>('.article-reading');
    const readingBar = page.querySelector<HTMLElement>('.reading-progress > i');
    const controls = page.querySelector<HTMLElement>('.landing-deliverables');
    const steps = [...page.querySelectorAll<HTMLElement>('.deliverable-grid article')];
    const layers = [...page.querySelectorAll<HTMLElement>('.control-display .boundary-layer')];
    const reveals = [...page.querySelectorAll<HTMLElement>('[data-immersion-reveal]')];
    let frame = 0;
    let observer: IntersectionObserver | undefined;
    let activeStep = -1;

    function paint() {
      frame = 0;
      if (!page || preference.matches) return;
      if (scene) {
        const rect = scene.getBoundingClientRect();
        if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
          const progress = clamp(-rect.top / rect.height);
          scene.style.setProperty('--scene-shift', `${(progress * 160).toFixed(2)}px`);
          scene.style.setProperty('--scene-turn', `${(progress * 24).toFixed(2)}deg`);
          scene.style.setProperty('--scene-spread', `${(progress * 110).toFixed(2)}px`);
        }
      }
      if (reading && readingBar) {
        const box = reading.getBoundingClientRect();
        const progress = clamp((window.innerHeight * .22 - box.top) / Math.max(1, box.height - window.innerHeight * .56));
        readingBar.style.transform = `scaleX(${progress.toFixed(4)})`;
      }
      if (controls && steps.length) {
        const rect = controls.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          let nearest = 0;
          let distance = Infinity;
          steps.forEach((step, index) => {
            const box = step.getBoundingClientRect();
            const current = Math.abs(box.top + box.height / 2 - window.innerHeight * .53);
            if (current < distance) { nearest = index; distance = current; }
          });
          if (nearest !== activeStep) {
            activeStep = nearest;
            steps.forEach((step, index) => { step.dataset.current = String(index === nearest); });
            layers.forEach((layer, index) => { layer.dataset.lit = String(index <= nearest); layer.dataset.current = String(index === nearest); });
          }
        }
      }
    }
    function schedule() { if (!frame && !preference.matches) frame = requestAnimationFrame(paint); }
    function configure() {
      observer?.disconnect();
      page!.dataset.immersionMotion = preference.matches ? 'reduced' : 'full';
      if (preference.matches) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        scene?.style.removeProperty('--scene-shift');
        scene?.style.removeProperty('--scene-turn');
        scene?.style.removeProperty('--scene-spread');
        reveals.forEach(item => { item.dataset.entered = 'true'; });
        layers.forEach(layer => { layer.dataset.lit = 'true'; layer.dataset.current = 'false'; });
        steps.forEach(step => { step.dataset.current = 'false'; });
        activeStep = -1;
      } else {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) { (entry.target as HTMLElement).dataset.entered = 'true'; observer?.unobserve(entry.target); }
          });
        }, { threshold: .04, rootMargin: '0px 0px -24px 0px' });
        reveals.forEach(item => { observer?.observe(item); });
        schedule();
      }
    }
    configure();
    preference.addEventListener('change', configure);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      preference.removeEventListener('change', configure);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      delete page.dataset.immersionMotion;
    };
  }, [enabled, root]);
}

export function DepthField() {
  return <div className="page-depth-field" aria-hidden="true">
    <img src="/images/ciso-world.webp" alt="" width="1536" height="1024" fetchPriority="high" />
    <div className="page-depth-field__light" />
    <div className="page-depth-field__floor" />
  </div>;
}

const artworkIcons = [ShieldCheck, BrainCircuit, Target, Layers3, Eye, BriefcaseBusiness];

export function InsightArtwork({ index, hero = false }: { index: number; hero?: boolean }) {
  const Icon = artworkIcons[index % artworkIcons.length];
  return <div className={`insight-artwork insight-artwork--${index % 6}${hero ? ' insight-artwork--hero' : ''}`} aria-hidden="true">
    <div className="insight-artwork__halo" />
    <div className="insight-artwork__sheets">
      <div className="insight-artwork__sheet insight-artwork__sheet--back"><i /><i /><i /></div>
      <div className="insight-artwork__sheet insight-artwork__sheet--middle"><i /><i /><i /></div>
      <div className="insight-artwork__sheet insight-artwork__sheet--front">
        <div className="insight-artwork__insignia"><Icon strokeWidth={.8} /></div>
        <div className="insight-artwork__rules"><i /><i /><i /></div>
        <span className="insight-artwork__seal" />
      </div>
    </div>
    <div className="insight-artwork__plinth" />
  </div>;
}

export function BoundarySculpture({ hero = false }: { hero?: boolean }) {
  return <div className={`boundary-sculpture${hero ? ' boundary-sculpture--hero' : ''}`} aria-hidden="true">
    <div className="boundary-sculpture__glow" />
    <div className="boundary-model">
      {Array.from({ length: 6 }, (_, index) => <div className="boundary-layer" key={index}>
        <i /><i /><i /><i />
        {index === 5 && <ShieldCheck className="boundary-layer__shield" strokeWidth={.75} />}
      </div>)}
      <div className="boundary-model__axis" />
    </div>
    <div className="boundary-sculpture__base" />
  </div>;
}
